import React, { useRef, useState } from 'react';
import { FloatingPanel, PanelSection } from '../ui/Panel';
import { Button } from '../ui/Button';
import { NumberInput, Slider, Checkbox } from '../ui/Input';
import { DRAINAGE_MODES, bearingLabel, ARENA_PRESETS } from '../../utils/grading';
import { usePanelMaxHeight } from '../ui/usePanelMaxHeight';


const muted = { color: '#6080a0', fontSize: '11px', lineHeight: 1.5 };
const yards = (v) => `${Math.round(v).toLocaleString()}`;
const feet = (v) => `${v.toFixed(1)}'`;

/**
 * GradingPanel - existing ground from drone data, arena outline, drainage
 * options and cut/fill comparison
 */
export const GradingPanel = ({
  terrain,
  hasGeoLayer,
  rooms,
  grading,
  onChangeGrading,
  onLoadTerrain,
  onRemoveTerrain,
  onAddArena,
  arenaShape,
  onResizeArena,
  result,
  onShow3D,
  siteModel,
  missingSiteModel,
  onLoadSiteModel,
  onRemoveSiteModel,
  onClose,
  isMobile = false,
}) => {
  const fileInputRef = useRef(null);
  const modelInputRef = useRef(null);
  const [modelLoading, setModelLoading] = useState(false);

  const handleModelFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setModelLoading(true);
    setError(null);
    try {
      await onLoadSiteModel(file);
    } catch (err) {
      setError(err.message || 'Failed to load the 3D model');
    } finally {
      setModelLoading(false);
    }
  };
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [measureRef, maxHeight] = usePanelMaxHeight();

  const set = (updates) => onChangeGrading({ ...grading, ...updates });
  const outlineRooms = rooms.filter(r => r.points?.length >= 3);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      await onLoadTerrain(file);
    } catch (err) {
      console.error('Failed to load terrain:', err);
      setError(err.message || 'Failed to load elevation data');
    } finally {
      setLoading(false);
    }
  };

  const active = result?.active;
  const params = active?.params;

  return (
    <FloatingPanel
      title="Site Grading"
      onClose={onClose}
      x={isMobile ? 10 : 100}
      y={isMobile ? 60 : 80}
      width={isMobile ? 300 : 330}
      maxHeight={maxHeight}
      isMobile={isMobile}
    >
      <div ref={measureRef} style={{ padding: '12px 14px' }}>
        {terrain && hasGeoLayer && (
          <Button variant="primary" onClick={onShow3D} style={{ width: '100%', marginBottom: '12px' }}>
            View in 3D
          </Button>
        )}
        <input ref={fileInputRef} type="file" accept=".ply" onChange={handleFile} style={{ display: 'none' }} />

        {error && (
          <div style={{
            background: 'rgba(255,80,80,0.2)', border: '1px solid rgba(255,80,80,0.5)', borderRadius: '6px',
            padding: '8px', marginBottom: '12px', color: '#ff6666', fontSize: '11px',
          }}>
            {error}
          </div>
        )}

        <PanelSection title="Existing ground">
          {!hasGeoLayer ? (
            <p style={muted}>
              Import the drone orthophoto first (PDF button → select the image and its .jgw world file).
              The elevation data is placed using the same survey coordinates.
            </p>
          ) : terrain ? (
            <>
              <p style={{ ...muted, color: '#a0b0c0' }}>
                {terrain.fileName} · {terrain.cols}×{terrain.rows} grid
              </p>
              <Button variant="danger" onClick={onRemoveTerrain} style={{ width: '100%', fontSize: '11px' }}>
                Remove elevation data
              </Button>

              <input ref={modelInputRef} type="file" accept=".glb" onChange={handleModelFile} style={{ display: 'none' }} />
              <div style={{ ...muted, marginTop: '10px', color: '#a0b0c0' }}>Drone 3D model (buildings and trees in the 3D view)</div>
              {siteModel ? (
                <>
                  <p style={{ ...muted, color: '#a0b0c0' }}>
                    {siteModel.fileName} · {(siteModel.size / 1e6).toFixed(0)} MB
                  </p>
                  <Button variant="default" onClick={onRemoveSiteModel} style={{ width: '100%', fontSize: '11px' }}>
                    Remove 3D model
                  </Button>
                </>
              ) : (
                <>
                  {missingSiteModel && (
                    <p style={{ ...muted, color: '#ffaa00' }}>
                      This project used {missingSiteModel}. 3D models aren't stored in project files; load it again.
                    </p>
                  )}
                  <Button variant="default" onClick={() => modelInputRef.current?.click()} disabled={modelLoading}
                    style={{ width: '100%', fontSize: '11px' }}>
                    {modelLoading ? 'Loading model…' : 'Load drone 3D model (.glb)'}
                  </Button>
                  <p style={muted}>
                    Made from the 3Dsurvey textured mesh (OBJ) with the site-model converter, so it is small enough
                    for the browser.
                  </p>
                </>
              )}
            </>
          ) : (
            <>
              <p style={muted}>
                Load the mesh exported from 3Dsurvey (binary .ply). It is turned into a 1 ft elevation grid.
              </p>
              <Button
                variant="primary"
                onClick={() => fileInputRef.current?.click()}
                disabled={loading}
                style={{ width: '100%' }}
              >
                {loading ? 'Processing mesh…' : 'Load elevation mesh (.ply)'}
              </Button>
            </>
          )}
        </PanelSection>

        {terrain && hasGeoLayer && (
          <>
            <PanelSection title="Arena outline">
              {outlineRooms.length > 0 && (
                <select
                  value={grading.roomId || ''}
                  onChange={(e) => set({ roomId: e.target.value || null })}
                  style={{
                    width: '100%', padding: '8px', marginBottom: '8px', background: '#1a2030', color: '#fff',
                    border: '2px solid rgba(0,200,255,0.3)', borderRadius: '6px', fontSize: '12px',
                  }}
                >
                  <option value="">Choose an outline…</option>
                  {outlineRooms.map(r => <option key={r.id} value={r.id}>{r.name || 'Room'}</option>)}
                </select>
              )}
              <Button variant="default" onClick={onAddArena} style={{ width: '100%', fontSize: '11px', marginBottom: '8px' }}>
                Add arena outline at view center
              </Button>

              {arenaShape && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '6px' }}>
                    <NumberInput label="Width (ft)" value={Math.round(arenaShape.widthFt * 10) / 10} min={10}
                      onChange={(v) => onResizeArena({ widthFt: v })} style={{ padding: '6px', fontSize: '12px' }} />
                    <NumberInput label="Length (ft)" value={Math.round(arenaShape.lengthFt * 10) / 10} min={10}
                      onChange={(v) => onResizeArena({ lengthFt: v })} style={{ padding: '6px', fontSize: '12px' }} />
                    <NumberInput label="Rotate (°)" value={Math.round(arenaShape.angleDeg * 10) / 10}
                      onChange={(v) => onResizeArena({ angleDeg: v })} style={{ padding: '6px', fontSize: '12px' }} />
                  </div>
                  <select
                    value=""
                    onChange={(e) => {
                      const preset = ARENA_PRESETS.find(p => p.id === e.target.value);
                      if (preset) {
                        onResizeArena({
                          widthFt: preset.widthFt,
                          lengthFt: preset.lengthFt,
                          cornerRadiusFt: preset.cornerRadiusFt,
                          alley: preset.alley || null,
                          presetId: preset.id,
                        });
                      }
                    }}
                    style={{
                      width: '100%', padding: '7px', marginBottom: '8px', background: '#1a2030', color: '#fff',
                      border: '2px solid rgba(0,200,255,0.3)', borderRadius: '6px', fontSize: '12px',
                    }}
                  >
                    <option value="">
                      {ARENA_PRESETS.find(p => p.id === arenaShape.presetId)?.label || 'Use a standard layout…'}
                    </option>
                    {ARENA_PRESETS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                  </select>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '6px' }}>
                    <NumberInput label="Corner radius" value={Math.round((arenaShape.cornerRadiusFt || 0) * 10) / 10} min={0}
                      onChange={(v) => onResizeArena({ cornerRadiusFt: v })} style={{ padding: '6px', fontSize: '12px' }} />
                    <div>
                      <label style={{ display: 'block', color: '#6080a0', fontSize: '10px', marginBottom: '4px' }}>Return alley</label>
                      <select
                        value={arenaShape.alley?.widthFt || 0}
                        onChange={(e) => {
                          const widthFt = Number(e.target.value);
                          onResizeArena({ alley: widthFt ? { side: arenaShape.alley?.side || 'right', widthFt } : null });
                        }}
                        style={{ width: '100%', padding: '8px 4px', background: '#1a2030', color: '#fff', border: '2px solid rgba(0,200,255,0.3)', borderRadius: '6px', fontSize: '12px' }}
                      >
                        <option value={0}>None</option>
                        <option value={6}>6 ft</option>
                        <option value={10}>10 ft</option>
                        <option value={12}>12 ft</option>
                      </select>
                    </div>
                    {arenaShape.alley && (
                      <div>
                        <label style={{ display: 'block', color: '#6080a0', fontSize: '10px', marginBottom: '4px' }}>Alley side</label>
                        <select
                          value={arenaShape.alley.side}
                          onChange={(e) => onResizeArena({ alley: { ...arenaShape.alley, side: e.target.value } })}
                          style={{ width: '100%', padding: '8px 4px', background: '#1a2030', color: '#fff', border: '2px solid rgba(0,200,255,0.3)', borderRadius: '6px', fontSize: '12px' }}
                        >
                          <option value="right">Right</option>
                          <option value="left">Left</option>
                        </select>
                      </div>
                    )}
                  </div>
                  <p style={muted}>
                    Priefert's layout drawings turn each corner with two angled 12' panels (about a 16 ft radius). The alley runs
                    along a long side and is fenced with the arena.
                  </p>
                </>
              )}
              {!arenaShape && grading.roomId && (
                <p style={{ ...muted, color: '#a0b0c0' }}>
                  This outline has been reshaped by hand, so size it by dragging its corners.
                </p>
              )}
              <p style={muted}>
                Drag the outline with the Select tool to move it. Select it to drag its corners, drag the small
                square on an edge to add a corner, or double-click a corner to remove it. Any room drawn with the Room tool can be used.
                Trees and buildings in the survey count as ground, so keep the outline over open ground.
              </p>
            </PanelSection>

            {result && (
              <>
                <PanelSection title="Drainage">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', marginBottom: '10px' }}>
                    {Object.entries(DRAINAGE_MODES).map(([mode, label]) => (
                      <Button key={mode} variant={grading.mode === mode ? 'primary' : 'default'}
                        style={{ fontSize: '11px', padding: '6px' }} onClick={() => set({ mode })}>
                        {label}
                      </Button>
                    ))}
                  </div>

                  {grading.mode === 'oneway' && (
                    <>
                      <Slider label="Slope" suffix="%" min={0} max={3} step={0.1}
                        value={grading.slopePercent} onChange={(v) => set({ slopePercent: v })} />
                      <Slider label={`Drains toward ${bearingLabel(grading.bearing)}`} suffix="°" min={0} max={355} step={5}
                        value={grading.bearing} onChange={(v) => set({ bearing: v })} />
                    </>
                  )}
                  {grading.mode === 'crown' && (
                    <Slider label="Fall from center to each long side" suffix="%" min={0.25} max={3} step={0.25}
                      value={grading.crownPercent} onChange={(v) => set({ crownPercent: v })} />
                  )}
                  {grading.mode === 'best' && (
                    <>
                      <Slider label="Minimum slope" suffix="%" min={0} max={3} step={0.25}
                        value={grading.minSlopePercent}
                        onChange={(v) => set({ minSlopePercent: v, maxSlopePercent: Math.max(v, grading.maxSlopePercent) })} />
                      <Slider label="Maximum slope" suffix="%" min={0} max={3} step={0.25}
                        value={grading.maxSlopePercent}
                        onChange={(v) => set({ maxSlopePercent: v, minSlopePercent: Math.min(v, grading.minSlopePercent) })} />
                      <p style={{ ...muted, color: '#00ffaa' }}>
                        Best: drains toward {params.bearing}° ({bearingLabel(params.bearing)}) at {params.slopePercent}%
                      </p>
                      <Button variant="default" style={{ width: '100%', fontSize: '11px', marginBottom: '8px' }}
                        onClick={() => set({ mode: 'oneway', bearing: params.bearing, slopePercent: params.slopePercent })}>
                        Fine-tune this as a one-way slope
                      </Button>
                    </>
                  )}

                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center', margin: '6px 0' }}>
                    <span style={muted}>Pad height:</span>
                    {['balance', 'manual'].map(m => (
                      <label key={m} style={{ ...muted, color: '#a0b0c0', display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <input type="radio" checked={grading.elevationMode === m}
                          onChange={() => set({
                            elevationMode: m,
                            manualElevation: grading.manualElevation ?? Math.round(active.padElevation * 10) / 10,
                          })} />
                        {m === 'balance' ? 'Balance cut & fill' : 'Set'}
                      </label>
                    ))}
                  </div>
                  {grading.elevationMode === 'manual' && (
                    <NumberInput label="Pad elevation at center" suffix="ft" step={0.1}
                      value={grading.manualElevation ?? Math.round(active.padElevation * 10) / 10}
                      onChange={(v) => set({ manualElevation: v })} />
                  )}
                </PanelSection>

                <PanelSection title="Compare (cubic yards)">
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                    <thead>
                      <tr style={{ color: '#6080a0', textAlign: 'right' }}>
                        <th style={{ textAlign: 'left', fontWeight: 400 }}>Option</th>
                        <th style={{ fontWeight: 400 }}>Cut</th>
                        <th style={{ fontWeight: 400 }}>Fill</th>
                        <th style={{ fontWeight: 400 }}>Deepest</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(DRAINAGE_MODES).map(([mode, label]) => {
                        const s = result.scenarios[mode];
                        const isActive = grading.mode === mode;
                        return (
                          <tr key={mode} onClick={() => set({ mode })} style={{
                            cursor: 'pointer', textAlign: 'right',
                            color: isActive ? '#00c8ff' : '#c0d0e0',
                            background: isActive ? 'rgba(0,200,255,0.08)' : 'transparent',
                          }}>
                            <td style={{ textAlign: 'left', padding: '4px 2px' }}>{label}</td>
                            <td style={{ color: isActive ? undefined : '#ff9a70' }}>{yards(s.cutYards)}</td>
                            <td style={{ color: isActive ? undefined : '#70a8ff' }}>{yards(s.fillYards)}</td>
                            <td>{feet(Math.max(s.maxCut, s.maxFill))}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <p style={{ ...muted, marginTop: '6px' }}>Click a row to show it on the map.</p>
                </PanelSection>

                <PanelSection title="Selected option">
                  <div style={{ ...muted, color: '#a0b0c0' }}>
                    <div>Arena area: {Math.round(result.area).toLocaleString()} sq ft</div>
                    <div>Existing ground: {feet(result.existingMin)} to {feet(result.existingMax)}</div>
                    <div>Pad elevation at center: {active.padElevation.toFixed(2)}'</div>
                    <div>Cut {yards(active.cutYards)} yd³ (deepest {feet(active.maxCut)}) · Fill {yards(active.fillYards)} yd³ (deepest {feet(active.maxFill)})</div>
                    {result.missingArea > 0 && (
                      <div style={{ color: '#ffaa00' }}>
                        {Math.round(result.missingArea).toLocaleString()} sq ft of the outline has no survey data and is not counted.
                      </div>
                    )}
                  </div>
                  <div style={{ marginTop: '8px' }}>
                    <Checkbox label="Show cut/fill map (red = cut, blue = fill)" checked={grading.showOverlay}
                      onChange={(v) => set({ showOverlay: v })} />
                  </div>
                  <p style={{ ...muted, marginTop: '6px' }}>
                    Volumes are inside the outline only. They don't include side slopes beyond the edge, topsoil stripping,
                    footing/base material or compaction (fill usually needs 10–25% more dirt than it measures).
                  </p>
                </PanelSection>
              </>
            )}

            {!result && grading.roomId && (
              <p style={{ ...muted, color: '#ffaa00' }}>The outline doesn't overlap the elevation data.</p>
            )}
          </>
        )}
      </div>
    </FloatingPanel>
  );
};

export default GradingPanel;
